import React, { useContext } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import ThisWeekButton from "./ThisWeekButton";
import NavButton from "./NavButton";
import WeeklistDropdown from "./WeeklistDropdown";
import { formatDate, startOfDay } from "../../../../../helpers/date";
import WidgetSettingsContext from "../../../../../contexts/WidgetSettingsContext";
import { getDateRange } from "../../../../../helpers/calendar";
import useWeekPeriod from "../../../../../hooks/useWeekPeriod";

const DateRangeSelector = (props) => {
  const { periodStart, periodEnd, addPeriods, isSamePeriod } = useWeekPeriod();

  const widgetSettings = useContext(WidgetSettingsContext);

  const eventsNumberOption = widgetSettings["eventsNumberOption"];
  const eventsNumberCount = widgetSettings["eventsNumber"];
  const isDateRange = eventsNumberOption === "date_range";

  const [startDate, endDate] = getDateRange(
    eventsNumberOption,
    eventsNumberCount
  );

  const currentWeek = periodStart(props.currentWeek);
  const currentWeekEnd = periodEnd(currentWeek);
  const isCurrentPeriod = isSamePeriod(currentWeek, new Date());

  const canChangeToPreviousWeeks = !isDateRange
    ? !(props.showUpcomingEventsOnly && isCurrentPeriod)
    : !startDate || currentWeek > startDate;

  const canChangeToNextWeeks = !isDateRange
    ? !props.showUpcomingEventsOnly
    : !endDate || startOfDay(currentWeekEnd) < startOfDay(endDate);

  /**
   *
   */
  const onClickThisWeek = () => {
    props.onChangeWeek(new Date());
  };

  /**
   *
   * @param direction {number} -1 previous period, 1 next period
   * @returns {function(): void}
   */
  const onDateChange = (direction) => () => {
    props.onChangeWeek(addPeriods(currentWeek, direction));
  };

  const previousWeekButton =
    props.allowChangingWeeks && canChangeToPreviousWeeks ? (
      <NavButton
        className={"prev"}
        icon={"keyboard_arrow_left"}
        onDateChange={onDateChange}
        direction={-1}
      />
    ) : null;

  const nextWeekButton =
    props.allowChangingWeeks && canChangeToNextWeeks ? (
      <NavButton
        className={"next"}
        icon={"keyboard_arrow_right"}
        onDateChange={onDateChange}
        direction={1}
      />
    ) : null;

  const weeksListDropdown = props.allowChangingWeeks ? (
    <WeeklistDropdown
      currentWeek={currentWeek}
      showUpcomingEventsOnly={props.showUpcomingEventsOnly}
      onChangeWeek={props.onChangeWeek}
      startDate={startDate}
      endDate={endDate}
    />
  ) : null;

  return (
    <div className={"date-range-selector--container"}>
      <ThisWeekButton currentWeek={currentWeek} onClick={onClickThisWeek} />

      <div className="date-range-selector__week-selector-arrow">
        <div className="date-range-selector__week-selector-arrow--navigation">
          {previousWeekButton}

          <div className={"date-range-selector-week-dropdown-container"}>
            <div
              className={classnames("date-range-selector-week-dropdown-value", {
                "without-weeks-dropdown": !props.allowChangingWeeks,
              })}
            >
              {formatDate(currentWeek, { month: "short", day: "numeric" })} -{" "}
              {formatDate(currentWeekEnd, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            {weeksListDropdown}
          </div>
          {nextWeekButton}
        </div>
      </div>
    </div>
  );
};

DateRangeSelector.propTypes = {
  currentWeek: PropTypes.object,
  onChangeWeek: PropTypes.func.isRequired,
  allowChangingWeeks: PropTypes.bool,
  showUpcomingEventsOnly: PropTypes.bool,
};

export default DateRangeSelector;
